/**
 * Fetch wrapper for the AI Memory API.
 *
 * Responsibilities:
 *  - Read JWT from sessionStorage and inject `Authorization: Bearer <jwt>`.
 *  - On HTTP 401, clear the JWT, trigger a one-shot re-exchange via the
 *    `reAuthenticate` callback supplied by `useAuth`, and retry the original
 *    request exactly once. A second 401 propagates as an `ApiError`.
 *  - Never log JWTs (Iron rule #8).
 *
 * The JWT lifecycle (set / clear / re-exchange) is owned by `useAuth`. The
 * client only reads through the storage interface and asks for a re-auth.
 */
import type { ApiErrorBody } from "../lib/types";

export const JWT_STORAGE_KEY = "aimemory:jwt";

/**
 * Optional absolute API origin (e.g. `https://api.example.com`). When set,
 * every request path is prefixed with it. When empty (the default), requests
 * stay relative — same-origin via the Vite dev proxy locally and via nginx
 * in the prod Docker target.
 *
 * Vite inlines `import.meta.env.*` at build time, so this is baked into the
 * static bundle. To change it, rebuild the frontend image with a new
 * `VITE_API_BASE_URL` build arg.
 */
const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? "")
  .toString()
  .replace(/\/+$/, "");

function buildUrl(path: string): string {
  if (!API_BASE_URL) {
    return path;
  }
  return path.startsWith("/") ? `${API_BASE_URL}${path}` : `${API_BASE_URL}/${path}`;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody | null;
  constructor(status: number, message: string, body: ApiErrorBody | null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/** Returns the currently-stored JWT or null. */
export function readJwt(): string | null {
  try {
    return window.sessionStorage.getItem(JWT_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Persist the JWT for the lifetime of the tab. */
export function writeJwt(token: string): void {
  try {
    window.sessionStorage.setItem(JWT_STORAGE_KEY, token);
  } catch {
    // Storage might be disabled — surface as an auth failure at the next request.
  }
}

/** Forget the JWT (logout / 401 / re-auth needed). */
export function clearJwt(): void {
  try {
    window.sessionStorage.removeItem(JWT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Callback the client calls when a 401 is observed. It must perform a fresh
 * `POST /api/v1/auth/telegram` and persist a new JWT (or throw if the SDK
 * still cannot give us initData). Set by `useAuth` on mount.
 */
let reAuthenticate: (() => Promise<void>) | null = null;

export function setReAuthenticator(fn: (() => Promise<void>) | null): void {
  reAuthenticate = fn;
  reAuthInFlight = null;
}

/**
 * Single-flight guard: when several requests hit 401 at once (the feed
 * poll racing an /ask on an expired JWT), they must share ONE re-auth
 * round-trip — parallel exchanges would each write a token, clobbering
 * each other and potentially leaving a request retrying with a token a
 * later exchange just replaced.
 */
let reAuthInFlight: Promise<void> | null = null;

function reAuthenticateOnce(fn: () => Promise<void>): Promise<void> {
  if (reAuthInFlight === null) {
    reAuthInFlight = fn().finally(() => {
      reAuthInFlight = null;
    });
  }
  return reAuthInFlight;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /** When true, do not inject the Authorization header (used by auth.ts). */
  skipAuth?: boolean;
  /** Internal — set to true when this is the post-401 retry. */
  _isRetry?: boolean;
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return typeof value === "object" && value !== null && "detail" in value;
}

function describeError(status: number, body: unknown): string {
  if (isApiErrorBody(body)) {
    if (typeof body.detail === "string") {
      return body.detail;
    }
    if (Array.isArray(body.detail) && body.detail.length > 0) {
      const first = body.detail[0];
      if (first && typeof first === "object" && "msg" in first && typeof first.msg === "string") {
        return first.msg;
      }
    }
  }
  return `HTTP ${status}`;
}

/** Low-level request used by all endpoint modules. */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, signal, skipAuth = false, _isRetry = false } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  headers["Accept"] = "application/json";

  // Remember which token this request was sent with — a 401 only means
  // *this* token is bad; a concurrent re-auth may already have replaced it.
  let jwtUsed: string | null = null;
  if (!skipAuth) {
    jwtUsed = readJwt();
    if (jwtUsed) {
      headers["Authorization"] = `Bearer ${jwtUsed}`;
    }
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: "same-origin",
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  if (signal !== undefined) {
    init.signal = signal;
  }

  const response = await fetch(buildUrl(path), init);

  if (response.status === 401 && !skipAuth && !_isRetry && reAuthenticate) {
    // A concurrent 401 may already have re-authed while this response was
    // in flight — if the stored token differs from the one we sent, just
    // retry with it instead of clearing the fresh token and re-exchanging.
    const stored = readJwt();
    if (stored !== null && stored !== jwtUsed) {
      return request<T>(path, { ...options, _isRetry: true });
    }
    clearJwt();
    try {
      await reAuthenticateOnce(reAuthenticate);
    } catch (cause) {
      const parsed = await parseBody(response);
      throw new ApiError(
        401,
        cause instanceof Error ? cause.message : "Re-authentication failed",
        isApiErrorBody(parsed) ? parsed : null,
      );
    }
    return request<T>(path, { ...options, _isRetry: true });
  }

  const parsed = await parseBody(response);

  if (!response.ok) {
    throw new ApiError(
      response.status,
      describeError(response.status, parsed),
      isApiErrorBody(parsed) ? parsed : null,
    );
  }

  return parsed as T;
}
